import { useState } from "react";
import { useAuth } from "../../context/AuthContext";

export default function ChangePasswordModal({ isOpen, onClose }) {
  const { changePassword } = useAuth();
  const [formData, setFormData] = useState({
    oldPassword: "",
    newPassword1: "",
    newPassword2: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.oldPassword) {
      newErrors.oldPassword = "Current password is required";
    }

    if (!formData.newPassword1) {
      newErrors.newPassword1 = "New password is required";
    } else if (formData.newPassword1.length < 8) {
      newErrors.newPassword1 =
        "Password must be at least 8 characters";
    }

    if (!formData.newPassword2) {
      newErrors.newPassword2 = "Please confirm your new password";
    } else if (formData.newPassword1 !== formData.newPassword2) {
      newErrors.newPassword2 = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await changePassword(
        formData.oldPassword,
        formData.newPassword1,
        formData.newPassword2
      );
      setIsSuccess(true);
      // Auto-close after 2 seconds
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      if (err.response?.data) {
        const apiErrors = err.response.data;
        const fieldErrors = {};

        // Map API errors to form fields
        if (apiErrors.old_password) {
          fieldErrors.oldPassword = Array.isArray(
            apiErrors.old_password
          )
            ? apiErrors.old_password[0]
            : apiErrors.old_password;
        }
        if (apiErrors.new_password1) {
          fieldErrors.newPassword1 = Array.isArray(
            apiErrors.new_password1
          )
            ? apiErrors.new_password1[0]
            : apiErrors.new_password1;
        }
        if (apiErrors.new_password2) {
          fieldErrors.newPassword2 = Array.isArray(
            apiErrors.new_password2
          )
            ? apiErrors.new_password2[0]
            : apiErrors.new_password2;
        }

        // General form error
        if (
          apiErrors.non_field_errors ||
          apiErrors.detail ||
          apiErrors.message
        ) {
          fieldErrors.form =
            apiErrors.non_field_errors?.[0] ||
            apiErrors.detail ||
            apiErrors.message;
        }

        setErrors(fieldErrors);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      oldPassword: "",
      newPassword1: "",
      newPassword2: "",
    });
    setErrors({});
    setIsSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center 
      justify-center p-4 bg-black bg-opacity-50">
      <div
        className="bg-surface rounded-lg shadow-xl max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 
          border-b border-divider">
          <h2 className="text-xl font-semibold text-primary-content">
            Change Password
          </h2>
          <button
            onClick={handleClose}
            className="text-secondary-content hover:text-primary-content
              transition-colors"
            aria-label="Close"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Success State */}
        {isSuccess ? (
          <div className="p-6 text-center space-y-4">
            <div
              className="w-16 h-16 mx-auto bg-positive 
                bg-opacity-10 rounded-full flex items-center 
                justify-center"
            >
              <svg
                className="w-8 h-8 text-positive"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-primary-content">
              Password Changed!
            </h3>
            <p className="text-secondary-content">
              Your password has been updated successfully.
            </p>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {errors.form && (
              <div
                className="p-3 bg-negative bg-opacity-10 
                  border border-negative rounded-md text-sm 
                  text-negative"
              >
                {errors.form}
              </div>
            )}

            {/* Current Password */}
            <div>
              <label
                htmlFor="oldPassword"
                className="block text-sm font-medium 
                  text-primary-content mb-1"
              >
                Current Password
              </label>
              <input
                type="password"
                id="oldPassword"
                name="oldPassword"
                value={formData.oldPassword}
                onChange={handleChange}
                className={`w-full px-3 py-2 bg-page border 
                  rounded-md text-primary-content
                  placeholder-secondary-content
                  focus:outline-none focus:ring-2 focus:ring-primary 
                  ${
                    errors.oldPassword
                      ? "border-negative"
                      : "border-divider"
                  }`}
                placeholder="Enter current password"
                autoFocus
              />
              {errors.oldPassword && (
                <p className="mt-1 text-sm text-negative">
                  {errors.oldPassword}
                </p>
              )}
            </div>

            {/* New Password */}
            <div>
              <label
                htmlFor="newPassword1"
                className="block text-sm font-medium 
                  text-primary-content mb-1"
              >
                New Password
              </label>
              <input
                type="password"
                id="newPassword1"
                name="newPassword1"
                value={formData.newPassword1}
                onChange={handleChange}
                className={`w-full px-3 py-2 bg-page border 
                  rounded-md text-primary-content
                  placeholder-secondary-content
                  focus:outline-none focus:ring-2 focus:ring-primary 
                  ${
                    errors.newPassword1
                      ? "border-negative"
                      : "border-divider"
                  }`}
                placeholder="Enter new password"
              />
              {errors.newPassword1 && (
                <p className="mt-1 text-sm text-negative">
                  {errors.newPassword1}
                </p>
              )}
            </div>

            {/* Confirm New Password */}
            <div>
              <label
                htmlFor="newPassword2"
                className="block text-sm font-medium 
                  text-primary-content mb-1"
              >
                Confirm New Password
              </label>
              <input
                type="password"
                id="newPassword2"
                name="newPassword2"
                value={formData.newPassword2}
                onChange={handleChange}
                className={`w-full px-3 py-2 bg-page border 
                  rounded-md text-primary-content
                  placeholder-secondary-content
                  focus:outline-none focus:ring-2 focus:ring-primary 
                  ${
                    errors.newPassword2
                      ? "border-negative"
                      : "border-divider"
                  }`}
                placeholder="Confirm new password"
              />
              {errors.newPassword2 && (
                <p className="mt-1 text-sm text-negative">
                  {errors.newPassword2}
                </p>
              )}
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-2 border border-divider 
                  text-primary-content rounded-md hover:bg-hover
                  transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 bg-primary text-white py-2 px-4 
                  rounded-md hover:bg-primary-hover focus:outline-none 
                  focus:ring-2 focus:ring-primary focus:ring-offset-2
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-colors font-medium"
              >
                {isLoading ? "Changing..." : "Change Password"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
